import React, { useState, useEffect, useCallback} from 'react';
import { updateDoc } from 'firebase/firestore';
import { collection, addDoc, getDocs, deleteDoc, doc, query, where, serverTimestamp } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { db, auth } from '../firebase';
import './clubchat.css';

function ClubChat() {
  const [user, loading] = useAuthState(auth);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [isClubLead, setIsClubLead] = useState(false);
  const [newChatHeading, setNewChatHeading] = useState('');
  const [newChatDescription, setNewChatDescription] = useState('');
  const [newChatMessage, setNewChatMessage] = useState('');
  const [error, setError] = useState(null);
  const [isCreatingNewChat, setIsCreatingNewChat] = useState(false);

  const fetchChats = useCallback(async () => {
    try {
      const chatsRef = collection(db, 'club_chats');
      const chatsSnapshot = await getDocs(chatsRef);
      const chatsList = chatsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setChats(chatsList);
      if (chatsList.length > 0 && !selectedChat && !isCreatingNewChat) {
        setSelectedChat(chatsList[0]);
      }
      console.log("Chats fetched:", chatsList);
    } catch (error) {
      console.error("Error fetching chats:", error);
      setError("Failed to fetch chats. Please try again.");
    }
  }, [selectedChat, isCreatingNewChat]);

  const checkIfClubLead = useCallback(async () => {
    if (user) {
      try {
        const clubLeadRef = collection(db, 'club_lead');
        const q = query(clubLeadRef, where('email', '==', user.email));
        const querySnapshot = await getDocs(q);
        const isLead = !querySnapshot.empty;
        setIsClubLead(isLead);
        console.log("Current user email:", user.email);
        console.log("Is club lead:", isLead);
      } catch (error) {
        console.error("Error checking club lead status:", error);
        setError("Failed to check club lead status. Please try again.");
      }
    }
  }, [user]);

  useEffect(() => {
    if (!loading) {
      fetchChats();
      checkIfClubLead();
    }
  }, [loading, fetchChats, checkIfClubLead]);

  const createNewChat = async (e) => {
    e.preventDefault();
    console.log("Attempting to create new chat:", newChatHeading);
    if (newChatHeading.trim() && newChatDescription.trim() && newChatMessage.trim() && isClubLead) {
      try {
        const chatData = {
          heading: newChatHeading,
          description: newChatDescription,
          message: newChatMessage,
          createdAt: serverTimestamp(),
          createdBy: user.uid,
          creatorName: user.displayName,
          creatorEmail: user.email,
          upvotes: 0,
          downvotes: 0,
          votedBy: {}
        };
        console.log("Chat data to be written:", chatData);
        
        const chatRef = await addDoc(collection(db, 'club_chats'), chatData);
        console.log("New chat created with ID:", chatRef.id);
        setNewChatHeading('');
        setNewChatDescription('');
        setNewChatMessage('');
        setIsCreatingNewChat(false);
        await fetchChats();
      } catch (error) {
        console.error("Error creating new chat:", error);
        setError(`Failed to create new chat. Error: ${error.message}`);
      }
    } else if (!isClubLead) {
      setError("You don't have permission to create a club chat.");
    } else {
      setError("Please fill in all fields (heading, description, and message).");
    }
  };

  const handleDelete = async (chatId) => {
    if (user && selectedChat && selectedChat.createdBy === user.uid) {
      try {
        const chatRef = doc(db, 'club_chats', chatId);
        await deleteDoc(chatRef);
        console.log("Chat deleted successfully");
        await fetchChats();
        setSelectedChat(null); // Clear selected chat after deletion
      } catch (error) {
        console.error("Error deleting chat:", error);
        setError("Failed to delete chat. Please try again.");
      }
    } else {
      setError("You don't have permission to delete this chat.");
    }
  };

  const handleVote = async (chatId, voteType) => {
    if (user) {
      try {
        const chatRef = doc(db, 'club_chats', chatId);
        const chat = chats.find(c => c.id === chatId);
        
        if (chat) {
          const userVote = chat.votedBy?.[user.uid] || null;
          let upvotes = chat.upvotes || 0;
          let downvotes = chat.downvotes || 0;
          let newVotedBy = { ...chat.votedBy } || {};

          if (userVote === voteType) {
            // User is un-voting
            if (voteType === 'up') upvotes--;
            else downvotes--;
            delete newVotedBy[user.uid];
          } else {
            // User is changing vote or voting for the first time
            if (userVote === 'up') upvotes--;
            if (userVote === 'down') downvotes--;
            if (voteType === 'up') upvotes++;
            else downvotes++;
            newVotedBy[user.uid] = voteType;
          }

          await updateDoc(chatRef, {
            upvotes,
            downvotes,
            votedBy: newVotedBy
          });

          await fetchChats();
          setSelectedChat(prevChat => ({
            ...prevChat,
            upvotes,
            downvotes,
            votedBy: newVotedBy
          }));
        }
      } catch (error) {
        console.error("Error voting:", error);
        setError("Failed to register vote. Please try again.");
      }
    } else {
      setError("Please log in to vote.");
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="ClubChat">
      <header>
        <h1>Club Chats</h1>
        {isClubLead && (
          <button onClick={() => {
            setSelectedChat(null);
            setIsCreatingNewChat(true);
          }} className="new-chat-button">
            New Chat
          </button>
        )}
      </header>
      {error && <div className="error-message">{error}</div>}
      
      <div className="chat-layout">
        <div className="chats-list">
          {chats.map(chat => (
            <div 
              key={chat.id} 
              className={`chat-item ${selectedChat && selectedChat.id === chat.id ? 'active' : ''}`}
              onClick={() => {
                setSelectedChat(chat);
                setIsCreatingNewChat(false);
              }}
            >
              <h3>{chat.heading || 'Untitled Chat'}</h3>
              <p>{(chat.description || '').substring(0, 50)}...</p>
            </div>
          ))}
        </div>
        <div className="chat-content">
          {isCreatingNewChat ? (
            <form onSubmit={createNewChat} className="new-chat-form">
              <input
                type="text"
                value={newChatHeading}
                onChange={(e) => setNewChatHeading(e.target.value)}
                placeholder="Enter chat heading"
                required
              />
              <textarea
                value={newChatDescription}
                onChange={(e) => setNewChatDescription(e.target.value)}
                placeholder="Enter chat description"
                required
              />
              <textarea
                value={newChatMessage}
                onChange={(e) => setNewChatMessage(e.target.value)}
                placeholder="Enter your club chat message"
                required
              />
              <button type="submit">Post Club Chat</button>
            </form>
          ) : selectedChat ? (
            <div className="selected-chat">
              <div className="chat-header">
                <h2>{selectedChat.heading || 'Untitled Chat'}</h2>
              </div>
              <div className="chat-body">
                <p className="chat-description">{selectedChat.description || 'No description provided.'}</p>
                <p className="chat-message">{selectedChat.message || 'No message provided.'}</p>
                <div className="chat-meta">
                  Posted by {selectedChat.creatorName || 'Unknown'} on {selectedChat.createdAt?.toDate().toLocaleString() || 'Unknown date'}
                </div>
                <div className="vote-buttons">
                  <button 
                    onClick={() => handleVote(selectedChat.id, 'up')}
                    className={selectedChat.votedBy?.[user?.uid] === 'up' ? 'voted' : ''}
                  >
                    👍 {selectedChat.upvotes || 0}
                  </button>
                  <button 
                    onClick={() => handleVote(selectedChat.id, 'down')}
                    className={selectedChat.votedBy?.[user?.uid] === 'down' ? 'voted' : ''}
                  >
                    👎 {selectedChat.downvotes || 0}
                  </button>
                </div>
                {user && selectedChat.createdBy === user.uid && (
                  <div className="delete-button">
                    <button onClick={() => handleDelete(selectedChat.id)}>Delete</button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p>Select a chat from the list or create a new chat.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClubChat;